import { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Building,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Clock,
  Filter,
  Download,
  Eye
} from 'lucide-react';
import '../styles/Finanzas.css';

const Finanzas = () => {
  const [currency, setCurrency] = useState('CLP');
  const [period, setPeriod] = useState('mes');
  const [themeColor, setThemeColor] = useState('#c4fffc');

  // Actualizar color del tema en tiempo real
  useEffect(() => {
    const updateThemeColor = () => {
      const color = localStorage.getItem('theme-primary-color') || '#c4fffc';
      setThemeColor(color);
    };

    updateThemeColor();
    const interval = setInterval(updateThemeColor, 200);
    window.addEventListener('theme-color-change', updateThemeColor);

    return () => {
      clearInterval(interval);
      window.removeEventListener('theme-color-change', updateThemeColor);
    };
  }, []);

  const exchangeRates = {
    CLP: 1,
    USD: 950,
    UF: 36500
  };

  const convertCurrency = (amountCLP, toCurrency) => {
    const amount = parseFloat(amountCLP);
    if (toCurrency === 'CLP') {
      return `$${amount.toLocaleString('es-CL')}`;
    } else if (toCurrency === 'USD') {
      const usdAmount = (amount / exchangeRates.USD);
      return `US$${usdAmount.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else if (toCurrency === 'UF') {
      const ufAmount = (amount / exchangeRates.UF);
      return `${ufAmount.toLocaleString('es-CL', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} UF`;
    }
    return `$${amount.toLocaleString('es-CL')}`;
  };

  const financialData = {
    ingresos: 450780000,
    gastos: 312560000,
    utilidad: 138220000,
    margenPromedio: 18.5,
    cuentasPorCobrar: 156780000,
    cuentasPorPagar: 89450000,
    flujoEfectivo: 67330000
  };

  const cuentasPendientes = [
    {
      id: 'FAC-2025-001',
      cliente: 'BHP Billiton',
      orden: 'OV-2025-001',
      monto: 123450000,
      fechaEmision: '2025-01-05',
      fechaVencimiento: '2025-02-04',
      estado: 'pagado',
      dias: 0
    },
    {
      id: 'FAC-2025-002',
      cliente: 'Anglo American',
      orden: 'OV-2025-004',
      monto: 78900000,
      fechaEmision: '2025-01-10',
      fechaVencimiento: '2025-02-09',
      estado: 'pendiente',
      dias: 26
    },
    {
      id: 'FAC-2025-003',
      cliente: 'Antofagasta Minerals',
      orden: 'OV-2025-005',
      monto: 56780000,
      fechaEmision: '2025-01-12',
      fechaVencimiento: '2025-02-11',
      estado: 'pendiente',
      dias: 28
    },
    {
      id: 'Pendiente',
      cliente: 'Minera Escondida',
      orden: 'OV-2025-002',
      monto: 45678900,
      fechaEmision: '2025-01-14',
      fechaVencimiento: '2025-02-13',
      estado: 'por-emitir',
      dias: 30
    },
    {
      id: 'Pendiente',
      cliente: 'Codelco División Andina',
      orden: 'OV-2025-003',
      monto: 23450000,
      fechaEmision: '2025-01-13',
      fechaVencimiento: '2025-02-12',
      estado: 'por-emitir',
      dias: 29
    }
  ];

  const gastosPorCategoria = [
    { categoria: 'Compras a Proveedores', monto: 189340000, porcentaje: 60.6 },
    { categoria: 'Logística y Transporte', monto: 43768000, porcentaje: 14.0 },
    { categoria: 'Personal', monto: 37507200, porcentaje: 12.0 },
    { categoria: 'Servicios Profesionales', monto: 21829600, porcentaje: 7.0 },
    { categoria: 'Gastos Generales', monto: 12505600, porcentaje: 4.0 },
    { categoria: 'Marketing', monto: 7609600, porcentaje: 2.4 }
  ];

  const getStatusColor = (estado) => {
    switch(estado) {
      case 'pagado': return 'status-paid';
      case 'pendiente': return 'status-pending';
      case 'por-emitir': return 'status-draft';
      case 'vencido': return 'status-overdue';
      default: return 'status-pending';
    }
  };

  const getStatusText = (estado) => {
    switch(estado) {
      case 'pagado': return 'Pagado';
      case 'pendiente': return 'Pendiente';
      case 'por-emitir': return 'Por Emitir';
      case 'vencido': return 'Vencido';
      default: return 'Pendiente';
    }
  };

  return (
    <div className="finanzas-container">
      <div className="finanzas-header">
        <div className="header-title">
          <DollarSign size={28} />
          <h1>Finanzas</h1>
        </div>
        <div className="header-controls">
          <select
            className="period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="semana">Esta semana</option>
            <option value="mes">Este mes</option>
            <option value="trimestre">Este trimestre</option>
            <option value="año">Este año</option>
          </select>
          <select
            className="currency-select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            <option value="CLP">CLP</option>
            <option value="USD">USD</option>
            <option value="UF">UF</option>
          </select>
        </div>
      </div>

      <div className="finanzas-stats">
        <div className="stat-card revenue">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <p>Ingresos</p>
            <h3>{convertCurrency(financialData.ingresos, currency)}</h3>
            <span className="stat-change positive">+12.5% vs mes anterior</span>
          </div>
        </div>

        <div className="stat-card expenses">
          <div className="stat-icon">
            <TrendingDown size={24} />
          </div>
          <div className="stat-content">
            <p>Gastos</p>
            <h3>{convertCurrency(financialData.gastos, currency)}</h3>
            <span className="stat-change negative">+5.2% vs mes anterior</span>
          </div>
        </div>

        <div className="stat-card profit">
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <p>Utilidad</p>
            <h3>{convertCurrency(financialData.utilidad, currency)}</h3>
            <span className="stat-change positive">+28.7% vs mes anterior</span>
          </div>
        </div>

        <div className="stat-card margin">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <p>Margen Promedio</p>
            <h3>{financialData.margenPromedio}%</h3>
            <span className="stat-change positive">+1.2% vs mes anterior</span>
          </div>
        </div>
      </div>

      <div className="finanzas-grid">
        <div className="cash-flow-card">
          <div className="card-header">
            <h3>Flujo de Efectivo</h3>
            <button className="btn-export">
              <Download size={16} />
            </button>
          </div>
          <div className="cash-flow-items">
            <div className="flow-item positive">
              <div className="flow-label">
                <CheckCircle size={16} />
                <span>Cuentas por Cobrar</span>
              </div>
              <div className="flow-amount">{convertCurrency(financialData.cuentasPorCobrar, currency)}</div>
            </div>
            <div className="flow-item negative">
              <div className="flow-label">
                <Clock size={16} />
                <span>Cuentas por Pagar</span>
              </div>
              <div className="flow-amount">-{convertCurrency(financialData.cuentasPorPagar, currency)}</div>
            </div>
            <div className="flow-item total">
              <div className="flow-label">
                <DollarSign size={16} />
                <span>Flujo Neto</span>
              </div>
              <div className="flow-amount">{convertCurrency(financialData.flujoEfectivo, currency)}</div>
            </div>
          </div>
        </div>

        <div className="expenses-breakdown-card">
          <div className="card-header">
            <h3>Gastos por Categoría</h3>
            <span className="total-expenses">{convertCurrency(financialData.gastos, currency)}</span>
          </div>
          <div className="expenses-list">
            {gastosPorCategoria.map((gasto, index) => (
              <div key={index} className="expense-item">
                <div className="expense-info">
                  <span className="expense-category">{gasto.categoria}</span>
                  <span className="expense-amount">{convertCurrency(gasto.monto, currency)}</span>
                </div>
                <div className="expense-bar">
                  <div
                    className="expense-fill"
                    style={{ width: `${gasto.porcentaje}%` }}
                  ></div>
                </div>
                <span className="expense-percentage">{gasto.porcentaje}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="accounts-section">
        <div className="section-header">
          <h3>Cuentas por Cobrar</h3>
          <div className="section-actions">
            <button className="btn-filter">
              <Filter size={16} />
              Filtrar
            </button>
            <button
              className="btn-export"
              style={{
                backgroundColor: themeColor,
                color: '#000',
                border: '2px solid #000'
              }}
            >
              <Download size={16} />
              Exportar
            </button>
          </div>
        </div>

        <div className="accounts-table-container">
          <table className="accounts-table">
            <thead>
              <tr>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Factura</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Cliente</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Orden</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Monto</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Emisión</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Vencimiento</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Días</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Estado</th>
                <th style={{ color: themeColor, padding: '12px', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {cuentasPendientes.map((cuenta, index) => (
                <tr key={index}>
                  <td className="invoice-cell">
                    {cuenta.id !== 'Pendiente' ? cuenta.id : (
                      <span className="pending-invoice">Por emitir</span>
                    )}
                  </td>
                  <td>
                    <div className="client-cell">
                      <Building size={14} />
                      {cuenta.cliente}
                    </div>
                  </td>
                  <td className="order-cell">{cuenta.orden}</td>
                  <td className="amount-cell">{convertCurrency(cuenta.monto, currency)}</td>
                  <td className="date-cell">
                    <Calendar size={14} />
                    {new Date(cuenta.fechaEmision).toLocaleDateString('es-CL')}
                  </td>
                  <td className="date-cell">
                    <Calendar size={14} />
                    {new Date(cuenta.fechaVencimiento).toLocaleDateString('es-CL')}
                  </td>
                  <td className="days-cell">
                    <span className={cuenta.dias < 15 ? 'days-good' : cuenta.dias < 25 ? 'days-warning' : 'days-danger'}>
                      {cuenta.dias} días
                    </span>
                  </td>
                  <td>
                    <span className={`status ${getStatusColor(cuenta.estado)}`}>
                      {getStatusText(cuenta.estado)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-action" title="Ver detalles">
                        <Eye size={16} />
                      </button>
                      {cuenta.estado === 'por-emitir' && (
                        <button className="btn-action btn-invoice" title="Emitir factura">
                          <CreditCard size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Finanzas;